import * as types from "./actionTypes";
import * as queueApi from "../api/queueApi";
import { failureMessage, isForbidden, isNotFound } from "../api/client";
import { showInfo, showNotice } from "./noticeActions";
import { loadQueueSongs, queueSongAdded } from "./queueActions";

const NAME_KEY = "requestName";

/** The name a guest last signed a request with, kept on the device. */
export const getRequestName = () => {
  try {
    return localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
};

const rememberRequestName = (name) => {
  try {
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  } catch {
    // storage unavailable: the name is asked for again next time
  }
};

let loadSequence = 0;

/** The pending requests of the session: the host decides on them, guests see what was asked for already. */
export const loadRequests = (code) => async (dispatch, getState) => {
  if (!code) return;
  const sequence = ++loadSequence;
  try {
    const requests = await queueApi.getRequests(code);
    if (sequence === loadSequence && getState().session.Code === code) dispatch({ type: types.REQUESTS_LOAD_SUCCESS, requests });
  } catch (error) {
    if (!isForbidden(error) && !isNotFound(error)) console.error("Could not load the requests", error);
  }
};

// pushed by the server (and applied locally from the REST response); a vote arrives as the same request with its new count
export const requestAdded = (request) => ({ type: types.REQUEST_ADDED, request });
export const requestRemoved = (requestId) => ({ type: types.REQUEST_REMOVED, requestId });

/** This device asked (or voted) for a song in the session, or takes that back; remembered per session on the device. */
export const requestAsked = (songId, asked = true) => ({ type: types.REQUEST_ASKED, songId, asked });

/** A guest asks for a song; the host sees it as a one-tap decision. */
export const requestSong = (code, song, name = "") => async (dispatch) => {
  const signed = (name || "").trim().slice(0, 50);
  rememberRequestName(signed);
  try {
    const request = await queueApi.requestSong(code, song.Id, signed || null);
    if (request) dispatch(requestAdded(request));
    dispatch(requestAsked(song.Id));
    dispatch(showInfo("Request sent", { sub: `${song.Title} · ${signed ? `from ${signed}` : "the host decides"}` }));
    return true;
  } catch (error) {
    dispatch(
      showNotice(
        isNotFound(error)
          ? "That song is no longer available."
          : failureMessage(error, "Couldn't send the request. Check your connection and try again.")
      )
    );
    return false;
  }
};

/** One more voice for a song someone else asked for (counted at once, settled by the server's count). */
export const voteRequest = (code, request) => async (dispatch, getState) => {
  if ((getState().requests.mine || []).includes(request.SongId)) return false;
  dispatch(requestAdded({ ...request, Votes: (request.Votes || 1) + 1 }));
  dispatch(requestAsked(request.SongId));
  try {
    const updated = await queueApi.requestSong(code, request.SongId, null);
    if (updated) dispatch(requestAdded(updated));
    dispatch(showInfo("Added your voice", { sub: `${request.SongTitle} · the host decides` }));
    return true;
  } catch (error) {
    dispatch(requestAdded(request));
    dispatch(requestAsked(request.SongId, false));
    dispatch(
      showNotice(
        isNotFound(error) ? "That request is gone." : failureMessage(error, "Couldn't add your voice. Check your connection and try again."),
        isNotFound(error) ? "info" : "error"
      )
    );
    return false;
  }
};

/** Queues a requested song right after the current one, or at the end (host only). */
export const acceptRequest = (code, request, atEnd = false) => async (dispatch) => {
  dispatch(requestRemoved(request.Id));
  try {
    const song = await queueApi.acceptRequest(code, request.Id, atEnd);
    dispatch(queueSongAdded(song));
    dispatch(loadQueueSongs(code));
    dispatch(showInfo(atEnd ? "Added to the end of the set" : "Queued next", { sub: song.Title }));
  } catch (error) {
    if (isNotFound(error)) {
      dispatch(showNotice("That request is gone.", "info"));
      return;
    }
    dispatch(requestAdded(request));
    dispatch(showNotice(isForbidden(error) ? "Only the host can accept requests." : failureMessage(error, "Couldn't queue the request. Check your connection and try again.")));
  }
};

/** Drops a request without queueing it (host only). */
export const dismissRequest = (code, request) => async (dispatch) => {
  dispatch(requestRemoved(request.Id));
  try {
    await queueApi.dismissRequest(code, request.Id);
  } catch (error) {
    if (isNotFound(error)) return;
    dispatch(requestAdded(request));
    dispatch(showNotice(failureMessage(error, "Couldn't dismiss the request. Check your connection and try again.")));
  }
};
